import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import {
  Globe,
  Briefcase,
  Building2,
  ArrowRight,
  UserPlus,
  LogIn,
  Loader2,
  CheckCircle,
  Languages,
  QrCode,
  MessageSquare,
  Phone,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languageNames = {
  en: 'English',
  es: 'Spanish',
  'zh-CN': 'Chinese',
  fr: 'French',
  de: 'German',
  pt: 'Portuguese',
  ar: 'Arabic',
  hi: 'Hindi',
  ja: 'Japanese',
  ko: 'Korean',
  ru: 'Russian'
};

export default function ConnectPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [connectInfo, setConnectInfo] = useState(null);
  const [error, setError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);

  // Quick signup form state
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [signingUp, setSigningUp] = useState(false);

  useEffect(() => {
    const fetchConnectInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/connect/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('invalid');
          } else {
            setError('server');
          }
          return;
        }

        const data = await response.json();
        setConnectInfo(data);
      } catch (err) {
        console.error('Error fetching connect info:', err);
        setError('network');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchConnectInfo();
    }
  }, [token]);

  // Auto-connect when authenticated user loads the page
  useEffect(() => {
    if (isAuthenticated && connectInfo && !connected && !connecting) {
      handleConnect();
    }
  }, [isAuthenticated, connectInfo]);

  const handleConnect = async () => {
    if (!isAuthenticated || connecting || connected) return;

    setConnecting(true);
    try {
      const response = await api.post(`/connect/${token}/add-contact`);
      setConnected(true);

      if (response.already_connected) {
        toast.success(`Already connected with ${connectInfo.displayName}`);
      } else {
        toast.success(`Connected with ${connectInfo.displayName}`);
      }
    } catch (err) {
      if (err.message?.includes('yourself')) {
        toast.error('This is your own connect link');
      } else {
        toast.error(err.message || 'Failed to connect');
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleStartDeal = async () => {
    try {
      const response = await api.post(`/connect/${token}/start-deal`);
      toast.success(`Deal created with ${connectInfo.displayName}`);
      navigate(`/deal/${response.deal._id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create deal');
    }
  };

  const handleQuickSignup = async (e) => {
    e.preventDefault();

    if (!signupData.name || !signupData.email || !signupData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSigningUp(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/${token}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }

      // Log the user in
      localStorage.setItem('token', data.token);

      toast.success(data.message || `Welcome! Connected with ${connectInfo.displayName}`);

      // Redirect to dashboard
      window.location.href = '/analytics';
    } catch (err) {
      toast.error(err.message || 'Signup failed');
    } finally {
      setSigningUp(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Skeleton className="h-20 w-20 rounded-full mx-auto mb-4" />
            <Skeleton className="h-6 w-48 mx-auto mb-2" />
            <Skeleton className="h-4 w-32 mx-auto" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <QrCode className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle>
              {error === 'invalid'
                ? 'Invalid Connect Link'
                : 'Something went wrong'
              }
            </CardTitle>
            <CardDescription>
              {error === 'invalid'
                ? 'This connect link is invalid or has expired.'
                : 'Please try again later.'
              }
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Quick signup form
  if (showSignup) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center pb-2">
            <Avatar className="h-16 w-16 mx-auto mb-3 ring-4 ring-primary/10">
              {connectInfo.avatarUrl ? (
                <AvatarImage src={connectInfo.avatarUrl} alt={connectInfo.displayName} />
              ) : (
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                  {connectInfo.displayName?.charAt(0)?.toUpperCase() || '?'}
                </AvatarFallback>
              )}
            </Avatar>
            <CardTitle className="text-lg">Join PocketFiler</CardTitle>
            <CardDescription>
              Connect with {connectInfo.displayName}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleQuickSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  placeholder="John Smith"
                  value={signupData.name}
                  onChange={(e) => setSignupData(d => ({ ...d, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={signupData.email}
                  onChange={(e) => setSignupData(d => ({ ...d, email: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 555 123 4567"
                  value={signupData.phone}
                  onChange={(e) => setSignupData(d => ({ ...d, phone: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={signupData.password}
                  onChange={(e) => setSignupData(d => ({ ...d, password: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={signingUp}
              >
                {signingUp ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Join and Connect
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Button
                variant="link"
                className="text-sm"
                onClick={() => setShowSignup(false)}
              >
                Back
              </Button>
            </div>
          </CardContent>

          <CardFooter className="justify-center border-t pt-4">
            <p className="text-xs text-muted-foreground text-center">
              By joining, you agree to PocketFiler's Terms of Service
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Main connect page - Authenticated user
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center pb-2">
            <Avatar className="h-20 w-20 mx-auto mb-4 ring-4 ring-primary/10">
              {connectInfo.avatarUrl ? (
                <AvatarImage src={connectInfo.avatarUrl} alt={connectInfo.displayName} />
              ) : (
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {connectInfo.displayName?.charAt(0)?.toUpperCase() || '?'}
                </AvatarFallback>
              )}
            </Avatar>

            {connected ? (
              <>
                <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Connected</span>
                </div>
                <CardTitle className="text-xl">{connectInfo.displayName}</CardTitle>
                {connectInfo.organizationName && (
                  <CardDescription>{connectInfo.organizationName}</CardDescription>
                )}
              </>
            ) : (
              <>
                <Badge variant="secondary" className="mb-2 mx-auto">
                  {connectInfo.type === 'user' ? (
                    <><UserPlus className="h-3 w-3 mr-1" /> Person</>
                  ) : (
                    <><Building2 className="h-3 w-3 mr-1" /> Organization</>
                  )}
                </Badge>
                <CardTitle className="text-xl">Connecting with</CardTitle>
                <h2 className="text-2xl font-bold text-primary mt-1">
                  {connectInfo.displayName}
                </h2>
                {connectInfo.organizationName && connectInfo.type === 'user' && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {connectInfo.organizationName}
                  </p>
                )}
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {connectInfo.primaryLanguages?.length > 0 && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Languages className="h-4 w-4" />
                <span>
                  {connectInfo.primaryLanguages.map(code => languageNames[code] || code).join(', ')}
                </span>
              </div>
            )}

            {connecting && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </div>
            )}

            {connected && (
              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={handleStartDeal}
                >
                  <Briefcase className="h-4 w-4 mr-2" />
                  Start a Deal
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/messages')}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Message
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/contacts')}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  View Contacts
                </Button>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Logged in as {user?.name || user?.email}</span>
            </div>
          </CardContent>

          <CardFooter className="justify-center border-t pt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Globe className="h-3 w-3" />
              <span>Powered by PocketFiler</span>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Main connect page - Unauthenticated user
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <Avatar className="h-20 w-20 mx-auto mb-4 ring-4 ring-primary/10">
            {connectInfo.avatarUrl ? (
              <AvatarImage src={connectInfo.avatarUrl} alt={connectInfo.displayName} />
            ) : (
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {connectInfo.displayName?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            )}
          </Avatar>

          <Badge variant="secondary" className="mb-2 mx-auto">
            {connectInfo.type === 'user' ? (
              <><UserPlus className="h-3 w-3 mr-1" /> Person</>
            ) : (
              <><Building2 className="h-3 w-3 mr-1" /> Organization</>
            )}
          </Badge>

          <CardTitle className="text-xl">
            {connectInfo.displayName}
          </CardTitle>
          <CardDescription>
            wants to connect with you on PocketFiler
          </CardDescription>

          {connectInfo.organizationName && connectInfo.type === 'user' && (
            <p className="text-sm text-muted-foreground mt-1">
              {connectInfo.organizationName}
            </p>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {connectInfo.primaryLanguages?.length > 0 && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Languages className="h-4 w-4" />
              <span>
                {connectInfo.primaryLanguages.map(code => languageNames[code] || code).join(', ')}
              </span>
            </div>
          )}

          <Alert className="bg-muted/50">
            <Globe className="h-4 w-4" />
            <AlertDescription>
              PocketFiler helps you manage deals and documents securely with automatic translation.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <Button
              className="w-full min-h-[48px]"
              onClick={() => setShowSignup(true)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Join Free and Connect
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>

            <Link to={`/login?redirect=/connect/${token}`} className="block">
              <Button variant="outline" className="w-full min-h-[48px]">
                <LogIn className="h-4 w-4 mr-2" />
                I Have an Account
              </Button>
            </Link>
          </div>
        </CardContent>

        <CardFooter className="justify-center border-t pt-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Globe className="h-3 w-3" />
            <span>Powered by PocketFiler</span>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
